/**
 * Hero image WebP responsive srcSet utilities.
 * Generates srcset strings for desktop and mobile hero banner images.
 */

/**
 * Cache-buster appended to every hero image URL.
 *
 * CF caches `/hero/*.webp` files at the edge for 24h. When we ship new
 * banner content under the same path, CF keeps serving the stale
 * version for the remaining TTL — observed 2026-05-11 when the Aura
 * Pine replacement was deployed but desktop kept showing Cozy because
 * `landscape-1.webp` was still in CF cache (age 13.7h, content-length
 * 82 KB vs the new 138 KB).
 *
 * Bump the timestamp suffix whenever a hero image's content changes
 * under an existing filename. CF treats `landscape-1.webp?v=20260511`
 * as a fresh URL, fetches from origin, and serves the new content.
 *
 * Format: `YYYYMMDDHH` so consecutive same-day updates can be
 * distinguished without manual counter management.
 */
export const HERO_CACHE_BUSTER = "v=2026051116";

/**
 * Add or replace the `?w=NNN` query param on a CDN-image-proxy URL.
 * Used for DB-driven hero slides hosted on cdn.diolo.com — the
 * worker accepts `?w=` for responsive resize AND auto-serves AVIF /
 * WebP based on the request's Accept header, so we don't need separate
 * `.webp` files in R2 for these.
 */
function appendCdnWidth(url: string, w: number): string {
  try {
    const u = new URL(url);
    u.searchParams.set("w", String(w));
    return u.toString();
  } catch {
    return url;
  }
}

/**
 * Generate a WebP srcSet for desktop hero images.
 *
 * Two URL shapes handled:
 *   - Local `/hero/<name>.jpg` — paired with pre-built `<name>-768.webp`
 *     etc. variants in `public/hero/`. Used for the hardcoded default
 *     slides.
 *   - CDN-hosted `https://cdn.diolo.com/<key>.jpg` — uses the
 *     cdn-image-proxy worker's `?w=NNN` resize. The worker negotiates
 *     AVIF/WebP via Accept header so a single .jpg in R2 covers every
 *     format. Used for DB-driven slides uploaded through admin.
 *
 * 2026-05-25: bug fix — previous version blindly swapped the .jpg
 * extension for `-768.webp` on CDN URLs too, generating srcSet entries
 * pointing at non-existent R2 objects. Browsers honoured the broken
 * `<source>` with 404s and either showed a placeholder or failed to
 * fall back to `<img src>` cleanly, so the new Monaco Summer banner
 * didn't render.
 */
export function heroWebpSrcSet(src: string, sizes = [768, 1280]): string {
  if (!src) return "";
  if (src.startsWith("https://cdn.diolo.com/")) {
    const responsive = sizes
      .map((w) => `${appendCdnWidth(src, w)} ${w}w`)
      .join(", ");
    return `${responsive}, ${appendCdnWidth(src, 1920)} 1920w`;
  }
  // Other absolute hosts (Shopify CDN, external) — return empty so the
  // browser skips the <source> and loads the <img src> directly. Better
  // than emitting a srcSet pointing at made-up .webp files.
  if (src.startsWith("http")) return "";
  // Local /hero/*.jpg — paired with build-time-generated .webp variants
  const base = src.replace(/\.[^.]+$/, "");
  const responsive = sizes
    .map((w) => `${base}-${w}.webp?${HERO_CACHE_BUSTER} ${w}w`)
    .join(", ");
  return `${responsive}, ${base}.webp?${HERO_CACHE_BUSTER} 1920w`;
}

/**
 * Generate a WebP srcSet for mobile hero images. Same dual-URL handling
 * as `heroWebpSrcSet` — see that function's docstring.
 */
export function heroMobileWebpSrcSet(src: string): string {
  if (!src) return "";
  if (src.startsWith("https://cdn.diolo.com/")) {
    // Mobile breakpoints: 480w for entry-level phones, 1280w for
    // mid-range, 2000w for 3× retina at full portrait width.
    return `${appendCdnWidth(src, 480)} 480w, ${appendCdnWidth(src, 1280)} 1280w, ${appendCdnWidth(src, 2000)} 2000w`;
  }
  if (src.startsWith("http")) return "";
  // Descriptors must match the actual file widths the build emits.
  // 2026-05-11 bumped from `768w` → `2000w` because the source mobile
  // images are now 2000×3000 (2:3 portraits) and the cwebp regen used
  // `-resize 2000 0` — telling the browser the larger variant is 2000w
  // lets it pick the appropriate file on retina displays (iPhone 14
  // at 3× DPI needs 1170px-actual; 2000w comfortably covers it without
  // upscaling).
  const base = src.replace(/\.[^.]+$/, "");
  return `${base}-480.webp?${HERO_CACHE_BUSTER} 480w, ${base}.webp?${HERO_CACHE_BUSTER} 2000w`;
}
